import React, { useEffect, useState } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';
import { useLocale, useTranslations } from 'next-intl';
import Banner from '@/components/commons/Banner';
import AddCustomerService from '@/components/commons/AddCustomerService';
import { countryFlagListImmigration, ImmgrationProps } from '@/components/immigration/MigrantContent';
import { scrollToElement } from '@/shared/utils/tools';
import { useRouter } from 'next/router';
import Head from '@/components/commons/Head';
import Image from 'next/image';
import Masonry from 'react-masonry-css';

// 签证列表数据 -- 数据有三级
export const visaTree: ImmgrationProps[] = [
  {
    value: '加拿大',
    label: '加拿大',
    enLabel: 'Canada',
    children: [
      {
        value: '临时探访签证',
        label: '临时探访签证',
        enLabel: 'Visitor Visa',
        children: [
          {
            value: '旅游签证',
            label: '旅游签证',
            enLabel: 'Visitor Visa for Travel',
            link: '/visa/short term visa/visitor visa/',
          },
          {
            value: '陪读签证',
            label: '陪读签证',
            enLabel: 'Visitor Record for Accompanying Person',
            link: '/study permit application 2/parents visa application and renew/',
          },
          {
            value: '商务考察签证',
            label: '商务考察签证',
            enLabel: 'Visitor Visa for Business Trip',
            link: '/visa/short term visa/business visit visa/',
          },
          {
            value: '超级探亲签证',
            label: '超级探亲签证',
            enLabel: 'Super Visa',
            link: '/visa/short term visa/super visa/',
          },
        ],
      },
      {
        value: '临时工作签证',
        label: '临时工作签证',
        enLabel: 'Temporary Work Permit',
        children: [
          {
            value: 'LMIA工作签证',
            label: 'LMIA工作签证',
            enLabel: 'LMIA Supported Work Permit',
            link: '/visa/other work permit/lmia work permit/',
          },
          {
            value: 'ICT跨国外派工签',
            label: 'ICT跨国外派工签',
            enLabel: 'Intra company Transferees Work Permit',
            link: '/work-permit-for-intra-company-transfer/',
          },
          {
            value: 'C11企业主工签',
            label: 'C11企业主工签',
            enLabel: 'C11 Work Permit',
            link: '/visa/other work permit/owner operator lmia/',
          },
          {
            value: '桥梁工签',
            label: '桥梁工签',
            enLabel: 'Bridging Work Permit',
            link: '/visa/other work permit/bridge work permit/',
          },
          {
            value: '配偶工签',
            label: '配偶工签',
            enLabel: 'Spouse Open Work Permit',
            link: '/visa/other work permit/spourse work permit/',
          },
        ],
      },
      {
        value: '留学相关签证',
        label: '留学相关签证',
        enLabel: 'Study Abroad Visa',
        children: [
          {
            value: '学习签证',
            label: '学习签证',
            enLabel: 'Study Permit',
            link: '/学签申请/',
          },
          {
            value: '毕业工签',
            label: '毕业工签',
            enLabel: 'PGWP',
            link: '/visa/student related work permit/post graduate work permit/',
          },
          {
            value: '实习工签',
            label: '实习工签',
            enLabel: 'Co op Work Permit',
            link: '/',
          },
          {
            value: '留学生配偶工签',
            label: '留学生配偶工签',
            enLabel: 'Spouse Open Work Permit',
            link: '/visa/student related work permit/student spouse work permit/',
          },
        ],
      },
      {
        value: '其他申请',
        label: '其他申请',
        enLabel: 'other visas',
        children: [
          {
            value: 'PR枫叶卡续签',
            label: 'PR枫叶卡续签',
            enLabel: 'PR Renewal',
            link: '/2025年加拿大枫叶卡更新（pr-card-renew）全攻略/',
          },
          {
            value: '加拿大入籍申请',
            label: '加拿大入籍申请',
            enLabel: 'Canadian Citizenship Application',
            link: '/2025年加拿大入籍全攻略/',
          },
        ],
      },
    ],
  },
  {
    value: '美国',
    label: '美国',
    enLabel: 'USA',
    children: [
      {
        value: '美国签证',
        label: '美国旅游签证',
        enLabel: 'US Visa Application',
        link: '/美国旅游签证（b-2）办理全指南/',
      },
    ],
  },
  {
    value: '亚洲',
    label: '亚洲',
    enLabel: 'Asia',
    children: [
      {
        value: '日本经验管理签证',
        label: '日本经验管理签证',
        enLabel: 'Japanese Immigration',
        link: '/日本经营管理签证/',
      },
    ],
  },
];

const VisaContent = () => {
  const t = useTranslations();
  const currentLocale = useLocale(); // 当前语言

  const { query } = useRouter();
  const { keyword, country, category } = query; // url连接上带的搜索关键词

  const [activeCountry, setActiveCountry] = useState<string>(''); //选中的国家
  const [secondCategoryList, setSecondCategoryList] = useState<ImmgrationProps[]>([]); //二级菜单
  const [searchSelectedItem, setSearchSelectedItem] = useState<ImmgrationProps | null>(null); //选中的搜索项
  const [searchValue, setSearchValue] = useState<string>('');

  useEffect(() => {
    if (country) {
      const tempActiveCountry = country as string;
      setActiveCountry(tempActiveCountry);
      const tempSecondCategoryList = visaTree.find((item) => item.value === tempActiveCountry)?.children || [];
      setSecondCategoryList(tempSecondCategoryList);
    } else {
      setActiveCountry(visaTree[0].value);
      setSecondCategoryList(visaTree[0]?.children || []);
    }
  }, [category, country]);

  // 查询叶子节点并设置状态的方法
  const findLeafNodeAndSetActive = (tree: ImmgrationProps[], query: string): void => {
    // 内部递归查找方法
    const findNode = (nodes: ImmgrationProps[], target: string, path: string[] = []): any => {
      // 将查询关键字转换为小写，确保大小写不敏感
      const lowerCaseTarget = target.toLowerCase();

      for (const node of nodes) {
        const newPath = [...path, node.value];

        // 将节点的值和标签转换为小写，进行不区分大小写的比较
        const lowerCaseValue = node.value.toLowerCase();
        const lowerCaseLabel = node.label.toLowerCase();
        const lowerCaseEnLabel = node.enLabel ? node.enLabel.toLowerCase() : '';

        // 检查是否与目标模糊匹配
        if (
          lowerCaseValue.includes(lowerCaseTarget) ||
          lowerCaseLabel.includes(lowerCaseTarget) ||
          lowerCaseEnLabel.includes(lowerCaseTarget)
        ) {
          // 如果找到叶子节点（无 children 或 children 为空）
          if (!node.children || node.children.length === 0) {
            return { leafNode: node, path: newPath };
          }
        }

        // 递归查找子节点
        if (node.children) {
          const result = findNode(node.children, target, newPath);
          if (result) return result;
        }
      }
      return null;
    };

    // 开始查找
    const result = findNode(tree, query);

    if (result) {
      console.log(result);
      const { leafNode, path } = result;

      setActiveCountry(path[0]);
      setSecondCategoryList(tree.find((node) => node.value === path[0])?.children || []);
      setSearchSelectedItem(leafNode);
    } else {
      console.warn('未找到匹配的叶子节点');
      setSearchSelectedItem(null);
    }
  };

  useEffect(() => {
    if (keyword) {
      // 如果有搜索关键字，执行搜索方法
      findLeafNodeAndSetActive(visaTree, keyword as string);
    }
  }, [keyword]);

  return (
    <div className="w-full">
      <Head
        title={activeCountry + '签证办理,顾问咨询'}
        description={
          '星环移民，专注加拿大移民和留学十余载，擅长量身定制方案，提供高性价比服务，成功率居行业领先水平。旗下拥有多位全职的ICCRC认证的「加拿大持牌移民顾问」、教育专家和职业培训导师，专业办理加拿大移民、留学、游学、夏令营、教育等服务，提供顾问咨询、签证办理、学校申请、就业指导、财税规划及新移民安家落地、置业创业等一条龙服务，对于解决各种签证、留学、移民疑难问题有丰富经验与成功案例。'
        }
        keywords="移民,海外移民,加拿大移民,全球移民,移民咨询,移民公司,移民中介,星环移民"
      />
      {/* 签证banner */}
      <Banner
        title={t('Visa.title')}
        subTitlle={t('Visa.titleEnglishText')}
        onSearch={(value: string) => {
          setSearchValue(value);
          if (value) {
            findLeafNodeAndSetActive(visaTree, value);
          }
        }}
        showError={{
          show: !searchSelectedItem && !!searchValue,
          message: t('Bannder.errorText'),
        }}
        onClickConsultCustomerService={() => {
          const element = document.getElementById('addCustomerServiceBox');
          if (element) {
            scrollToElement(element);
          }
        }}
      />
      {/* 签证内容 */}
      <div className="box-border w-full bg-[#E7EEFF] px-[9.36vw] pb-[6.5vw] pt-[1.56vw] max-md:-mt-[138px] max-md:rounded-t-3xl max-md:px-[4%] max-md:pb-[29px] max-md:pt-[18px]">
        {/* 国家级分类 */}
        <div className="mb-[2.6vw] flex w-full flex-row flex-wrap items-center justify-start max-md:mb-7 max-md:grid max-md:grid-cols-4 max-md:gap-0">
          {visaTree.map((item, index) => (
            <h2
              className={`mb-[0.5vw] mr-[2.86vw] flex cursor-pointer flex-row items-center justify-center px-[2.288vw] py-[0.728vw] text-center max-md:m-0 max-md:p-0 max-md:py-[9px] ${activeCountry === item.value ? 'rounded-[28px] bg-[#2A2B45] text-[1.144vw] leading-[1.144vw] text-white max-md:rounded-[33px] max-md:text-sm max-md:leading-[14px]' : 'text-[1.04vw] leading-[1.04vw] text-[#333333] max-md:text-sm max-md:leading-[14px]'}`}
              key={index}
              onClick={() => {
                setActiveCountry(item.value);
                setSecondCategoryList(item.children || []);
              }}
            >
              {countryFlagListImmigration.find((country) => country.value === item.value) && (
                <Image
                  src={`/images/home/${countryFlagListImmigration.find((country) => country.value === item.value)?.enValue}.png`}
                  width={34}
                  height={34}
                  className="mr-[0.78vw] size-[1.768vw] max-md:mr-2 max-md:size-5"
                  alt="country"
                />
              )}
              {currentLocale === 'zh' ? item.label : item.enLabel}
            </h2>
          ))}
        </div>
        {/* 签证分类 */}
        {secondCategoryList && secondCategoryList.length > 0 ? (
          <>
            {activeCountry && activeCountry === '加拿大' ? (
              <>
                <Masonry
                  breakpointCols={2}
                  className="my-masonry-grid max-md:hidden"
                  columnClassName="my-masonry-grid_column"
                >
                  {(secondCategoryList || []).map((item, index) => {
                    return (
                      <div key={index} className="mb-[2.08vw] rounded-[1.04vw] bg-[#33367C] max-md:mb-6">
                        <h2 className="box-border flex h-[4.316vw] flex-row items-center justify-start px-[1.872vw] text-left text-[1.248vw] font-medium text-white max-md:h-8 max-md:px-4 max-md:text-[14px]">
                          {item.label}
                        </h2>
                        <div className="rounded-[1.04vw] bg-white px-[1.82vw] pb-[2.756vw] pt-[2.34vw] max-md:p-6 max-md:px-4 max-md:py-4">
                          {(activeCountry === '加拿大' ? item?.children || [] : secondCategoryList || [])?.length >
                          0 ? (
                            <div>
                              {(activeCountry === '加拿大' ? item?.children || [] : secondCategoryList || []).map(
                                (_item, index) => (
                                  <div
                                    className="mb-[1.976vw] flex cursor-pointer flex-row justify-start text-[0.936vw] font-medium text-[#2A2B45] last:mb-0 hover:font-semibold hover:text-[#19228C] max-md:text-xs"
                                    key={index}
                                    onClick={() => {
                                      if (_item?.link) {
                                        const fullLink = _item.link.startsWith('http')
                                          ? _item.link
                                          : process.env.NEXT_PUBLIC_WEBSITE + _item.link;
                                        window.open(fullLink, '_blank');
                                      }
                                    }}
                                  >
                                    <span className="mr-[1.04vw] w-2/5">{_item.label}</span>
                                    <span className="flex-1">{_item.enLabel}</span>
                                  </div>
                                ),
                              )}
                            </div>
                          ) : (
                            <div className="text-center max-md:text-xs">{t('Home.noData')}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Masonry>
                <Masonry
                  breakpointCols={1}
                  className="my-masonry-grid md:hidden"
                  columnClassName="my-masonry-grid_column"
                >
                  {(secondCategoryList || []).map((item, index) => {
                    return (
                      <div key={index} className="mb-[2.08vw] rounded-[1.04vw] bg-[#33367C] max-md:mb-6">
                        <h2 className="box-border flex h-[4.316vw] flex-row items-center justify-start px-[1.872vw] text-left text-[1.248vw] font-medium text-white max-md:h-8 max-md:px-4 max-md:text-[14px]">
                          {item.label}
                        </h2>
                        <div className="rounded-[1.04vw] bg-white px-[1.82vw] pb-[2.756vw] pt-[2.34vw] max-md:p-6 max-md:px-4 max-md:py-4">
                          {(activeCountry === '加拿大' ? item?.children || [] : secondCategoryList || [])?.length >
                          0 ? (
                            <div>
                              {(activeCountry === '加拿大' ? item?.children || [] : secondCategoryList || []).map(
                                (_item, index) => (
                                  <div
                                    className="mb-[1.976vw] flex cursor-pointer flex-row justify-start text-[0.936vw] font-medium text-[#2A2B45] last:mb-0 hover:font-semibold hover:text-[#19228C] max-md:text-xs"
                                    key={index}
                                    onClick={() => {
                                      if (_item?.link) {
                                        const fullLink = _item.link.startsWith('http')
                                          ? _item.link
                                          : process.env.NEXT_PUBLIC_WEBSITE + _item.link;
                                        window.open(fullLink, '_blank');
                                      }
                                    }}
                                  >
                                    <span className="mr-[1.04vw] w-2/5">{_item.label}</span>
                                    <span className="flex-1">{_item.enLabel}</span>
                                  </div>
                                ),
                              )}
                            </div>
                          ) : (
                            <div className="text-center max-md:text-xs">{t('Home.noData')}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </Masonry>
              </>
            ) : (
              <div className="w-[36.92vw] max-md:w-full">
                <div className="mb-[2.08vw] rounded-[1.04vw] bg-[#33367C] max-md:mb-6">
                  <h2 className="box-border flex h-[4.316vw] flex-row items-center justify-start px-[1.872vw] text-left text-[1.248vw] font-medium text-white max-md:h-8 max-md:px-4 max-md:text-[14px]">
                    {activeCountry}
                  </h2>
                  <div className="rounded-[1.04vw] bg-white px-[1.82vw] pb-[2.756vw] pt-[2.34vw] max-md:p-6 max-md:px-4 max-md:py-4">
                    {(secondCategoryList || [])?.length > 0 ? (
                      <div>
                        {(secondCategoryList || []).map((_item, index) => (
                          <div
                            className="mb-[1.976vw] flex cursor-pointer flex-row justify-start text-[0.936vw] font-medium text-[#2A2B45] last:mb-0 hover:font-semibold hover:text-[#19228C] max-md:text-xs"
                            key={index}
                            onClick={() => {
                              if (_item?.link) {
                                const fullLink = _item.link.startsWith('http')
                                  ? _item.link
                                  : process.env.NEXT_PUBLIC_WEBSITE + _item.link;
                                window.open(fullLink, '_blank');
                              }
                            }}
                          >
                            <span className="mr-[1.04vw] w-2/5">{_item.label}</span>
                            <span>{_item.enLabel}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center max-md:text-xs">{t('Home.noData')}</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center max-md:text-xs">{t('Home.noData')}</div>
        )}
      </div>
      {/* 添加客服 */}
      <AddCustomerService />
    </div>
  );
};

export default VisaContent;
